import { Controller } from "@hotwired/stimulus"
import { copyTextToClipboard, selectHighlightFilter } from "../reader_annotations.js"
import { rememberHighlightColor, selectColorButtons, validHighlightColor } from "../highlight_colors.js"
import { requestJson } from "../reader_api.js"

const HIGHLIGHT_STATUS_DURATION = 5000

export default class extends Controller {
  showHighlightStatus(label = this.labelsValue.selectTextToHighlight) {
    this.highlightStatusTarget.textContent = label
    this.highlightStatusTimer = window.setTimeout(() => this.clearHighlightStatus(), HIGHLIGHT_STATUS_DURATION)
  }

  clearHighlightStatus() {
    window.clearTimeout(this.highlightStatusTimer)
    this.highlightStatusTimer = null
    this.highlightStatusTarget.textContent = ""
  }

  dismissSelectionOnBackdrop(event) {
    if (event.target === this.selectionDialogTarget) this.dismissSelection(event)
  }

  selectionClosePreservesPendingSelection(event) {
    if (event?.type !== "close" || !this.preserveSelectionOnClose) return false

    this.preserveSelectionOnClose = false
    return true
  }

  translationConnected() {
    this.setTranslationOffline(false)
  }

  translationDisconnected() {
    this.setTranslationOffline(true)
  }

  setTranslationOffline(offline) {
    if (!this.hasTranslationButtonTarget) return

    this.translationOffline = offline
    this.translationButtonTarget.disabled = offline
    this.translationButtonTarget.setAttribute("aria-disabled", String(offline))
    this.translationAvailabilityTarget.hidden = !offline
    this.translationAvailabilityTarget.textContent = offline ? this.labelsValue.translationOffline : ""
    this.translationSubmitTarget.disabled = Boolean(offline || this.translationPending)
    this.translationLanguageTarget.disabled = Boolean(offline || this.translationPending)
    if (this.translationDialogTarget.open && offline) this.translationStatusTarget.textContent = this.labelsValue.translationOffline
    if (!offline && this.translationStatusTarget.textContent === this.labelsValue.translationOffline) this.translationStatusTarget.textContent = ""
  }

  async highlightSelection() {
    await this.persistSelection(false)
  }

  async copySelection() {
    const copied = await copyTextToClipboard(this.pendingSelection?.selectedText)
    this.selectionStatusTarget.textContent = copied ? this.labelsValue.selectionCopied : this.labelsValue.selectionCopyFailed
  }

  openTranslation() {
    if (!this.pendingSelection || this.translationOffline || this.translationButtonTarget.disabled) return

    this.resetTranslation()
    this.translationOriginalTarget.textContent = this.pendingSelection.selectedText
    this.preserveSelectionOnClose = true
    this.selectionDialogTarget.close()
    this.translationDialogTarget.showModal()
    this.translationLanguageTarget.focus()
  }

  async translateSelection(event) {
    event.preventDefault()
    if (!this.pendingSelection || this.translationOffline || this.translationSubmitTarget.disabled) return

    const requestId = (this.translationRequestId || 0) + 1
    this.translationRequestId = requestId
    this.translationPending = true
    this.translationSubmitTarget.disabled = true
    this.translationLanguageTarget.disabled = true
    this.translationDialogTarget.setAttribute("aria-busy", "true")
    this.translationStatusTarget.textContent = this.labelsValue.translationLoading
    this.translationResultTarget.hidden = true
    this.translationCopyStatusTarget.textContent = ""

    let errorLabel = this.labelsValue.translationError
    const translation = await requestJson(this.translationUrlValue, {
      method: "POST",
      onHttpError: (response) => {
        if (response.status !== 429) return false
        errorLabel = this.labelsValue.translationRateLimited
        return true
      },
      body: {
        translation: {
          text: this.pendingSelection.selectedText,
          target_language: this.translationLanguageTarget.value
        }
      }
    })

    if (this.translationRequestId !== requestId) return

    this.translationPending = false
    this.translationSubmitTarget.disabled = Boolean(this.translationOffline)
    this.translationLanguageTarget.disabled = Boolean(this.translationOffline)
    this.translationDialogTarget.removeAttribute("aria-busy")
    if (!translation?.translated_text) {
      this.translationStatusTarget.textContent = errorLabel
      return
    }

    this.translationTextTarget.textContent = translation.translated_text
    this.translationStatusTarget.textContent = ""
    this.translationResultTarget.hidden = false
  }

  async copyTranslation() {
    const copied = await copyTextToClipboard(this.translationTextTarget.textContent)
    this.translationCopyStatusTarget.textContent = copied ? this.labelsValue.translationCopied : this.labelsValue.translationCopyFailed
  }

  backToSelection() {
    if (!this.pendingSelection) return

    this.preserveTranslationOnClose = true
    this.translationDialogTarget.close()
    this.selectionDialogTarget.showModal()
  }

  dismissTranslation(event) {
    event?.preventDefault()
    if (event?.type === "close" && this.preserveTranslationOnClose) {
      this.preserveTranslationOnClose = false
      return
    }

    if (this.translationDialogTarget.open) this.translationDialogTarget.close()
    this.resetTranslation()
    this.dismissSelection()
  }

  dismissTranslationOnBackdrop(event) {
    if (event.target === this.translationDialogTarget) this.dismissTranslation(event)
  }

  resetTranslation() {
    this.translationRequestId = (this.translationRequestId || 0) + 1
    this.translationPending = false
    this.translationLanguageTarget.value = this.translationLanguageTarget.dataset.defaultLanguage || ""
    this.translationStatusTarget.textContent = ""
    this.translationCopyStatusTarget.textContent = ""
    this.translationTextTarget.textContent = ""
    this.translationResultTarget.hidden = true
    this.translationSubmitTarget.disabled = Boolean(this.translationOffline)
    this.translationLanguageTarget.disabled = Boolean(this.translationOffline)
    this.translationDialogTarget.removeAttribute("aria-busy")
  }

  selectSelectionColor(event) {
    const color = event.currentTarget.dataset.highlightColor
    if (!validHighlightColor(this.highlightColorsValue, color)) return

    this.pendingHighlightColor = color
    rememberHighlightColor(color)
    selectColorButtons(this.selectionColorTargets, color)
  }

  async addNoteToSelection() {
    await this.persistSelection(true)
  }

  closeSidebarAfterNavigation() {
    return this.readerSidebar.closeAfterNavigation()
  }

  filterHighlights(event) {
    this.highlightFilterColor = selectHighlightFilter({
      color: event.currentTarget.dataset.highlightColor,
      palette: this.highlightColorsValue,
      buttons: this.highlightFilterTargets
    })
    this.renderHighlights()
  }

  escape(value) {
    const element = document.createElement("div")
    element.textContent = value
    return element.innerHTML
  }
}
